import React, { useEffect, useState } from "react"

import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import { useSignUpModalState } from "@fuse/sharedComponents/globalQuickSignUpModal"
import InputField from "@fuse/sharedComponents/inputField"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import { EButtonType, EPlatform, TWebUser } from "@letshang/constants"
import { useRecoilState } from "recoil"

import { updateUserStateFromBackend, userState } from "@fuse/recoil/signup"

import { updateUserDetails } from "@api/firebaseWrappedApis"

import { useEvents } from "@fuse/pages/eventPage/controller"

const NameComponent = ({
  closeModal,
  from = "joinEvent",
  verifyEmail = false,
  setStep,
}: {
  closeModal?: () => void
  from?: "createEvent" | "joinEvent"
  verifyEmail?: boolean
  setStep?: (step: number) => void
}) => {
  const [user, setUser] = useRecoilState(userState)
  const [name, setTheName] = useState<string>("")

  const [isLoading, setIsLoading] = useState(false)

  const handleOnChange = (value: string) => {
    setUser((prev: TWebUser) => ({ ...prev, name: value }))
  }
  const { setFastSignUpStage, setEventParams } = useSignUpModalState()
  const { plusOnePopupState, setPlusOnePopupState, plusOnes } = useEvents(
    user.eventId,
  )

  const plusOneAllowedNumber = plusOnes?.allowedNumber ?? [0, 1]
  const isAllowed = plusOnes?.isAllowed ?? false

  const min = plusOneAllowedNumber[0]
  const max = plusOneAllowedNumber[1]

  const handleJoinEvent = async () => {
    const userObj = {
      name: name,
      expoPushToken: user.expoPushToken,
      country: user.country,
      phoneNumber: user.phone,
      firstName: name,
    }
    try {
      setIsLoading(true)
      const res: any = await updateUserDetails({
        ...userObj,
        eventId: user.eventId,
        //   added missing dependency to avoid the ts error
        id: "",
        email: "",
        phone: "",
        dateOfBirth: "",
        profileImage: "",
        events: [],
        platform: EPlatform.WEB,
      })

      if (res.success) {
        await updateUserStateFromBackend()
        setUser((prev: TWebUser) => ({
          ...prev,
          isOnboarded: true,
          isLoggedIn: true,
        }))
        setEventParams({ plusOneCount: plusOnePopupState.plusOneCount })
        if (verifyEmail) {
          setStep(4)
          return
        }
        setFastSignUpStage("done")
        closeModal()
      } else {
        console.error("Error updating user details")
        showErrorToast(
          "Unexpected error occurred, please close this window and try again. If the problem persists, please contact support.",
        )
      }
    } catch (e) {
      console.error(e)
      showErrorToast(
        "Unexpected error occurred, please close this window and try again. If the problem persists, please contact support.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  const [err, setError] = useState("")

  const validateName = (name: string): string | null => {
    if (!name || typeof name !== "string") {
      return "Name is required"
    }

    // Trim whitespace
    const trimmed = name.trim()

    // Check if empty after trim
    if (trimmed.length === 0) {
      return "Name cannot be empty or only spaces"
    }

    // Check minimum length
    if (trimmed.length < 2) {
      return "Name must be at least 2 characters long"
    }

    // Check maximum length
    if (trimmed.length > 50) {
      return "Name cannot exceed 50 characters"
    }

    // Check for numbers
    if (/\d/.test(trimmed)) {
      return "Name cannot contain numbers"
    }

    // Check for emojis (comprehensive emoji ranges)
    const emojiRegex =
      /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]|[\u{E000}-\u{F8FF}]|[\u{FE00}-\u{FE0F}]|[\u{1F004}\u{1F0CF}\u{1F18E}\u{1F191}-\u{1F19A}\u{1F1E6}-\u{1F1FF}\u{1F201}\u{1F21A}\u{1F22F}\u{1F232}-\u{1F236}\u{1F238}-\u{1F23A}\u{1F250}\u{1F251}\u{1F300}-\u{1F320}\u{1F32D}-\u{1F335}\u{1F337}-\u{1F37C}\u{1F37E}-\u{1F393}\u{1F3A0}-\u{1F3CA}\u{1F3CF}-\u{1F3D3}\u{1F3E0}-\u{1F3F0}\u{1F3F4}\u{1F3F8}-\u{1F43E}\u{1F440}\u{1F442}-\u{1F4FC}\u{1F4FF}-\u{1F53D}\u{1F54B}-\u{1F54E}\u{1F550}-\u{1F567}\u{1F57A}\u{1F595}\u{1F596}\u{1F5A4}\u{1F5FB}-\u{1F64F}\u{1F680}-\u{1F6C5}\u{1F6CC}\u{1F6D0}-\u{1F6D2}\u{1F6EB}\u{1F6EC}\u{1F6F4}-\u{1F6FA}\u{1F7E0}-\u{1F7EB}\u{1F90D}-\u{1F93A}\u{1F93C}-\u{1F945}\u{1F947}-\u{1F971}\u{1F973}-\u{1F976}\u{1F97A}-\u{1F9A2}\u{1F9A5}-\u{1F9AA}\u{1F9AE}-\u{1F9CA}\u{1F9CD}-\u{1F9FF}\u{1FA70}-\u{1FA73}\u{1FA78}-\u{1FA7A}\u{1FA80}-\u{1FA82}\u{1FA90}-\u{1FA95}]/u
    if (emojiRegex.test(trimmed)) {
      return "Name cannot contain emojis"
    }

    // Check for brackets and other disallowed special characters
    if (/[(){}\[\]<>]/.test(trimmed)) {
      return "Name cannot contain brackets or special characters like (), [], {}, <>"
    }

    // Check for other problematic characters
    if (/[#@$%^*+=|\\/`~]/.test(trimmed)) {
      return "Name cannot contain special characters like #, @, $, %, ^, *, +, =, |, \\, /, `, ~"
    }

    // Check for multiple consecutive spaces
    if (/\s{2,}/.test(trimmed)) {
      return "Name cannot have multiple consecutive spaces"
    }

    // Check if it contains only valid characters (Unicode letters, marks, apostrophes, hyphens, single spaces, dots, commas)
    if (!/^[\p{L}\p{M}'\-\s.,]+$/u.test(trimmed)) {
      return "Name contains invalid characters. Only letters, apostrophes, hyphens, spaces, and basic punctuation are allowed"
    }

    // Check that it has at least one letter (not just punctuation and spaces)
    if (!/\p{L}/u.test(trimmed)) {
      return "Name must contain at least one letter"
    }

    return null
  }

  const handleSubmit = () => {
    const nameError = validateName(name)
    if (nameError) {
      setError(nameError)
    } else {
      handleJoinEvent()
    }
  }

  useEffect(() => {
    if (name && err) {
      handleOnChange(name)
      setError("")
    }
  }, [name])

  const getCtaBtnTitle = (from: string) => {
    switch (from) {
      case "createEvent":
        return "Create"
      case "joinEvent":
        return "Join"
      default:
        return "Join"
    }
  }

  return (
    <div className="flex gap-6 flex-col">
      <div className="relative z-[10]">{isLoading && <LoadingSpinner />}</div>
      <div className="text-left">
        <p className="text-white/35 text-sm mb-1">Your name</p>
        <div className="px-4 bg-white/20 h-12 rounded-md flex w-full items-center">
          <input
            type="text"
            placeholder="John Doe"
            className={`w-full h-full bg-transparent text-white text-[15px] placeholder-white/35 outline-none`}
            autoFocus
            value={name}
            onChange={(e) => setTheName(e.target.value)}
          />
        </div>
        {err && (
          <div className={`text-red-500 text-left font-medium text-xs py-1`}>
            {err}
          </div>
        )}
        {from === "joinEvent" ? (
          <p className="text-sm text-white/35 opacity-50 mt-1 mb-8">
            The host would love to know your name so they can spot you easily.
          </p>
        ) : null}
        {isAllowed && (
          <div>
            <p className="text-white/35 text-sm mb-1">Add a +1 (optional)</p>
            <select
              className="w-full p-3 rounded-lg bg-white/30 backdrop-blur-md text-white border-none focus:outline-none"
              value={plusOnePopupState.plusOneCount}
              onChange={(e) => {
                setPlusOnePopupState((prev) => ({
                  ...prev,
                  plusOneCount: Number(e.target.value),
                }))
              }}
            >
              {Array.from({ length: max - min + 1 }, (_, i) => (
                <option key={i} value={min + i}>
                  {min + i}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <Button
        variant={EButtonType.solid}
        className="py-2 mt-6"
        onClick={handleSubmit}
        disabled={isLoading}
      >
        <div className="text-white font-semibold font-sans">
          {getCtaBtnTitle(from)}
        </div>
      </Button>
    </div>
  )
}

export default NameComponent
